/**
 * Carte des vins › Traductions (/carte/traductions) — on part d'un modèle, on choisit la
 * langue cible et les options, l'IA (Paramètres › Intégrations) produit la traduction et
 * crée/ met à jour l'édition (PDF + carte web + QR). Pas de relecture, pas de statut.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Languages, RefreshCw } from 'lucide-react';
import { carteApi } from '../../services/api';
import { Button, Spinner } from '../../components/ui/kit';

const LANGS = { fr: 'Français', en: 'English', nl: 'Nederlands', de: 'Deutsch', es: 'Español' };
const SCOPES = [
  ['titres', 'Titres de couleur et de région'], ['mentions', 'Mentions (cépage, verre…)'],
  ['descriptions', 'Descriptions'], ['accords', 'Accords mets-vins'], ['entete', 'En-tête et pied de page'],
];

export default function Traductions() {
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery({ queryKey: ['carte-templates'], queryFn: carteApi.settings });
  const [tid, setTid] = useState(null);
  const [lang, setLang] = useState(null);
  const [scopes, setScopes] = useState(Object.fromEntries(SCOPES.map(([k]) => [k, true])));
  const [busy, setBusy] = useState(false);

  const model = templates.find((t) => t.id === tid) || templates[0];
  const effTid = tid || templates[0]?.id;
  const sourceLang = model?.language || 'fr';
  const effLang = lang && lang !== sourceLang ? lang : Object.keys(LANGS).find((l) => l !== sourceLang);

  const { data: prev } = useQuery({
    queryKey: ['tr-preview', effTid, effLang], enabled: !!effTid && !!effLang,
    queryFn: () => carteApi.translationPreview(effTid, effLang),
  });

  const create = async () => {
    setBusy(true);
    try {
      const res = await carteApi.translateTemplate(effTid, effLang, { scopes });
      if (res.needs_key) { toast.error('Aucune clé IA configurée (Paramètres › Intégrations).'); return; }
      toast.success(`Édition ${LANGS[effLang]} créée · ${res.translated_now} segment(s)`);
      qc.invalidateQueries({ queryKey: ['tr-preview', effTid, effLang] });
      qc.invalidateQueries({ queryKey: ['carte-editions'] });
    } catch { toast.error('Échec de la traduction'); } finally { setBusy(false); }
  };

  if (!templates.length) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="p-4 md:p-6 grid lg:grid-cols-[380px_1fr] gap-6 max-w-[1100px]">
      <div>
        <label className="block mb-4"><span className="kicker block mb-1">Modèle source</span>
          <select className="vd-input h-9 w-full" value={effTid} onChange={(e) => { setTid(e.target.value); setLang(null); }} data-testid="tr-model">
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {LANGS[t.language]}</option>)}
          </select>
          <span className="text-[11px] text-n600 mt-1 block">Classement, ligne cuvée, prix, typographie et pages ajoutées sont repris du modèle.</span>
        </label>

        <p className="kicker mb-1">Langue cible</p>
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="tr-langs">
          {Object.entries(LANGS).filter(([l]) => l !== sourceLang).map(([l, name]) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`tr-lang-${l}`}
              className={`px-3 h-9 rounded border text-[13px] transition-colors ${effLang === l ? 'bg-gold100 border-gold' : 'border-hair hover:bg-surface'}`}>{name}</button>
          ))}
        </div>

        <p className="kicker mb-1">À traduire</p>
        <div className="mb-3" data-testid="tr-scopes">
          {SCOPES.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-[13px] py-0.5 cursor-pointer">
              <input type="checkbox" checked={scopes[k]} onChange={(e) => setScopes({ ...scopes, [k]: e.target.checked })} className="accent-gold" /> {label}
            </label>
          ))}
          <p className="text-[11px] text-n600 mt-1">Jamais traduits : cuvées, domaines, appellations, cépages.</p>
        </div>

        <p className="text-[12px] text-n600 mb-3 inline-flex items-center gap-1">
          <Languages className="w-3.5 h-3.5" /> Moteur : IA configurée dans <Link to="/parametres/ia" className="vd-link">Paramètres › Intégrations</Link>
        </p>

        <Button variant="primary" className="h-10 w-full" onClick={create} disabled={busy} data-testid="tr-create">
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> {prev?.has_translation ? `Mettre à jour l'édition ${effLang?.toUpperCase()}` : `Créer l'édition ${LANGS[effLang]}`}
        </Button>
        <p className="text-[11px] text-n600 mt-2">Traduit par l'IA, puis ajouté dans Éditions : PDF, carte web, QR code.</p>
      </div>

      <div className="vd-card p-4" data-testid="tr-preview">
        <p className="font-title text-xl mb-3">Aperçu {sourceLang.toUpperCase()} ↔ {effLang?.toUpperCase()}</p>
        {busy && <div className="py-6"><Spinner /></div>}
        {!busy && !prev?.has_translation && <p className="text-[13px] text-n600 py-6">La traduction n'existe pas encore. Cliquez sur « Créer l'édition ».</p>}
        {!busy && prev?.has_translation && (
          <div className="divide-y divide-hair">
            {prev.rows.filter((r) => r.translated).slice(0, 30).map((r) => (
              <div key={r.key} className="grid grid-cols-2 gap-4 py-2 text-[13px]">
                <span className="text-n600">{r.source}</span><span>{r.translated}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
